import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'ghost' | 'outline' | 'danger';
type Size = 'sm' | 'md';

const VARIANT: Record<Variant, string> = {
  primary:
    'bg-accent text-[#04160a] border border-accent hover:bg-accent/85 disabled:bg-accent/40 disabled:border-accent/40',
  outline:
    'border border-line-bright bg-transparent text-ink hover:border-accent hover:text-accent',
  ghost: 'border border-transparent bg-transparent text-ink-dim hover:bg-raised hover:text-ink',
  danger: 'border border-alert/50 bg-transparent text-alert hover:bg-alert-dim',
};

const SIZE: Record<Size, string> = {
  sm: 'h-7 px-2.5 text-[11px]',
  md: 'h-9 px-3.5 text-xs',
};

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
};

export function Button({
  variant = 'outline',
  size = 'md',
  icon,
  className,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      type="button"
      {...props}
      className={cn(
        'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-sharp font-mono font-medium uppercase tracking-[0.1em] transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANT[variant],
        SIZE[size],
        className,
      )}
    >
      {icon}
      {children}
    </button>
  );
}

/** Compact square button for row-level actions. */
export function IconButton({
  active = false,
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }) {
  return (
    <button
      type="button"
      {...props}
      className={cn(
        'inline-flex size-7 items-center justify-center rounded-sharp border transition-colors',
        active
          ? 'border-accent/60 bg-accent-dim text-accent'
          : 'border-transparent text-ink-faint hover:border-line-bright hover:bg-raised hover:text-ink',
        'disabled:cursor-not-allowed disabled:opacity-40',
        className,
      )}
    >
      {children}
    </button>
  );
}
